import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Input from "../Input/Input";
import Button from "../Button/Button";
import { useFormik } from "formik";
import * as Yup from "yup";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  firstName: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .required("First name is required"),
  lastName: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .required("Last name is required"),
});

const UserModal = ({
  open,
  onClose,
  onSubmit,
  user = null,
  mode = "create",
}) => {
  const isEditMode = mode === "edit";

  const formik = useFormik({
    initialValues: {
      email: user?.email || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await onSubmit(values);
        if (!isEditMode) {
          resetForm();
        }
      } catch (error) {
        console.error("Error in form submission:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="text-xl font-semibold border-b">
        {isEditMode ? "Edit User" : "Create New User"}
      </DialogTitle>
      <DialogContent className="mt-4 !py-6">
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <Input
            name="email"
            label="Email Address"
            placeholder="user@reverie3d.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            errorText={formik.touched.email && formik.errors.email}
            containerClassName="w-full"
            disabled={isEditMode}
          />

          <Input
            name="firstName"
            label="First Name"
            placeholder="Enter first name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.firstName && Boolean(formik.errors.firstName)
            }
            errorText={formik.touched.firstName && formik.errors.firstName}
            containerClassName="w-full"
          />

          <Input
            name="lastName"
            label="Last Name"
            placeholder="Enter last name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            errorText={formik.touched.lastName && formik.errors.lastName}
            containerClassName="w-full"
          />
        </form>

        {!isEditMode && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> A temporary password will be sent to the user's email. They will be required to change it upon first login.
            </p>
          </div>
        )}
      </DialogContent>
      <DialogActions className="border-t !px-6 !py-4 gap-2">
        <Button
          onClick={handleClose}
          className="bg-gray-200 text-gray-700 !px-6"
        >
          Cancel
        </Button>
        <Button
          onClick={formik.handleSubmit}
          loading={formik.isSubmitting}
          primary
          className="!px-6"
        >
          {isEditMode ? "Save Changes" : "Create User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserModal;
